/**
 * @fileoverview Maker-checker rules for new prospects.
 * @description A Super Admin's or Sales Head's new prospect is live at once. A Sales Manager's or
 * Sales Executive's (the maker) waits for approval from their Sales Head (the checker) — found by
 * walking up their reporting line — and Super Admin may approve any pending prospect. Until it's
 * approved, a prospect appears only in Sales Pipeline's Pending Approval tab; once approved, the
 * assignee the maker chose (always the maker themselves, for a Sales Executive) becomes its owner,
 * and it waits in the Backlog until a target is applied.
 * Shared by the mock API and the UI so both apply the same rules.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { SalesUserRecord, Tenant } from '@/features/sales/types/tenant.types'

/**
 * @description Whether a prospect created by someone in this role needs a Sales Head's approval.
 * @param {string} makerRole - The creator's active role name.
 * @returns {boolean} True for Sales Managers and Sales Executives.
 */
export function prospectNeedsApproval(makerRole: string): boolean {
  return makerRole === 'Sales Manager' || makerRole === 'Sales Executive'
}

/**
 * @description The Sales Head above a user in the reporting line, who approves their prospects.
 * @param {string} makerId - The creator's roster id.
 * @param {SalesUserRecord[]} roster - The full sales roster.
 * @returns {string | null} The Sales Head's roster id, or null if the line doesn't reach one.
 */
export function prospectApproverFor(makerId: string, roster: SalesUserRecord[]): string | null {
  const seen = new Set<string>()
  let current = roster.find((user) => user.id === makerId)
  while (current?.reportsTo && !seen.has(current.id)) {
    seen.add(current.id)
    const managerId: string = current.reportsTo
    current = roster.find((user) => user.id === managerId)
    if (current?.role === 'Sales Head') return current.id
  }
  return null
}

/**
 * @description Whether a prospect is still waiting for approval.
 * @param {Pick<Tenant, 'approval'>} tenant - The prospect.
 * @returns {boolean} True while it's pending.
 */
export function isPendingApproval(tenant: Pick<Tenant, 'approval'>): boolean {
  return tenant.approval === 'Pending Approval'
}

/**
 * @description Whether a viewer may approve a pending prospect: its own Sales Head, or Super Admin.
 * @param {Pick<Tenant, 'approval' | 'approverId'>} tenant - The prospect.
 * @param {string} viewerUserId - The viewer's roster id.
 * @param {string} viewerRole - The viewer's active role name.
 * @returns {boolean} True when the viewer may approve it now.
 */
export function canApproveProspect(
  tenant: Pick<Tenant, 'approval' | 'approverId'>,
  viewerUserId: string,
  viewerRole: string,
): boolean {
  if (!isPendingApproval(tenant)) return false
  return (
    viewerRole === 'Super Admin' || (Boolean(viewerUserId) && tenant.approverId === viewerUserId)
  )
}
